// Reading a plan, whole.
//
// ---------------------------------------------------------------------------
// REFETCH, NOT PATCH
// ---------------------------------------------------------------------------
//
// `docs/archived/09-web-client.md` puts "client-side merge of pushed content" on the list of
// things the implementer must not decide, and gives the reason in a sentence worth keeping
// in front of whoever changes this file: "every piece of client-side state is a place the
// client can disagree with the server about what a family said."
//
// So a signal produces a call to `loadSnapshot`, which reads the plan, the threads, the
// people, the decisions, the open actions and the messages, and REPLACES the whole
// snapshot. There is no reconciliation anywhere in this package, no message merge, no
// "insert if newer", no last-write-wins. The whole of the client's state is the last
// snapshot the server described.
//
// ---------------------------------------------------------------------------
// THE ONE THING THAT SURVIVES A REFETCH: THE DEPTH
// ---------------------------------------------------------------------------
//
// A reader who has paged back through six months of conversation and then receives a
// signal must not be yanked to the bottom. The obvious fix is to merge the new page into
// the pages already held, which is exactly the merge logic that is forbidden.
//
// What is held instead is a NUMBER: how many pages deep the reader has asked to be. A
// refetch re-reads that many pages from the server in one pass, following the server's own
// `nextBefore` cursors, and replaces everything. No page is merged with an older copy of
// itself, no cursor is computed here, and the concatenation is contiguous because each
// page is defined by the cursor the previous page returned. New messages arriving between
// two refetches shift where the page boundaries fall and cannot duplicate or drop a
// message, which is acceptance case 3.
//
// ---------------------------------------------------------------------------
// WHICH THREAD
// ---------------------------------------------------------------------------
//
// The URL says where the reader is, and the URL chooses the thread (`ThreadChoice`): on the
// home and family pages, the reader's own plan-level conversation; inside a topic, that
// topic's family conversation the moment one exists, else its principals' conversation, else
// the reader's own, with `/private` and `/principals` on the address overriding. Always by
// `kind` and `topic`, never by recency and never by inference from who has been invited:
// the thread the server actually put this caller in is the fact.
//
// On an issue's pages the thread is the reader's own conversation about that issue, found by
// the issue's id, and nothing else.
//
// If the chosen thread is closed there is no composer and it is shown read-only.

import type { ApiClient } from '../api/client.js';
import type {
  Decision,
  Message,
  PendingAction,
  Person,
  Plan,
  Objective,
  Thread,
  TopicView,
} from '../api/contract.js';

export type PlanSnapshot = {
  readonly plan: Plan;
  readonly threads: readonly Thread[];
  /** The thread being read, or null when this caller participates in none. */
  readonly thread: Thread | null;
  readonly messages: readonly Message[];
  readonly people: readonly Person[];
  readonly decisions: readonly Decision[];
  readonly objectives: readonly Objective[];
  /** The plan's eleven topics, in order, as this reader may see them. */
  readonly topics: readonly TopicView[];
  /**
   * Every open action this reader can see, which is every open action they may answer.
   *
   * Not filtered here, and not filterable here: the server serves the list already narrowed
   * to conversations the reader is in, so there is no rule in this package about who sees
   * what to get wrong or to drift.
   */
  readonly pendingActions: readonly PendingAction[];
  /** The server's cursor for the next older page, or null at the beginning of time. */
  readonly nextBefore: string | null;
  /** How many pages were read. Carried so the next refetch reads the same depth. */
  readonly depth: number;
};

/**
 * Which conversation the screen is reading, taken from the address: the topic's key (null
 * for the plan-level screens) and the room the address names, if it names one.
 */
export type ThreadChoice = {
  readonly topic: string | null;
  readonly room: 'private' | 'principals' | null;
  /** The issue whose conversation the screen shows, on an issue's pages. */
  readonly issue: string | null;
};

export type LoadSnapshot = (
  client: ApiClient,
  planId: string,
  depth: number,
  choice: ThreadChoice,
) => Promise<PlanSnapshot>;

/** The newest thread still accepting messages, else the newest thread, else null. */
export function chooseThread(threads: readonly Thread[]): Thread | null {
  const ordered = [...threads].sort((a, b) => compare(a, b));
  const open = ordered.filter((thread) => thread.acceptingMessages);
  return open[open.length - 1] ?? ordered[ordered.length - 1] ?? null;
}

/** The topic's family conversation, or null before the family is in. Null topic: plan-level. */
export function groupThreadOf(
  threads: readonly Thread[],
  topic: string | null,
): Thread | null {
  return (
    threads.find((thread) => thread.kind === 'shared' && thread.topic === topic) ?? null
  );
}

export function principalsThreadOf(
  threads: readonly Thread[],
  topic: string | null,
): Thread | null {
  return (
    threads.find((thread) => thread.kind === 'principals' && thread.topic === topic) ??
    null
  );
}

/**
 * The reader's own 1:1 with the facilitator in this topic (or at the plan level when
 * `topic` is null) — their `preparing` thread if they made the plan, their `private`
 * thread otherwise. The API returns only threads this caller participates in, and a solo
 * thread is by construction theirs alone, so "not a group thread" is enough to find it.
 */
export function ownThreadOf(
  threads: readonly Thread[],
  topic: string | null,
): Thread | null {
  return (
    threads.find(
      (thread) =>
        thread.topic === topic &&
        thread.kind !== 'shared' &&
        thread.kind !== 'principals' &&
        thread.issue === null,
    ) ?? null
  );
}

/** The reader's own conversation about one issue, or null before it has been opened. */
export function issueThreadOf(threads: readonly Thread[], issue: string): Thread | null {
  return threads.find((thread) => thread.issue === issue) ?? null;
}

/** A family member: the reader is on the list without the same say as the people planning. */
export function viewerLacksSay(snapshot: Pick<PlanSnapshot, 'people'>): boolean {
  return snapshot.people.some((person) => person.self && !person.sameSay);
}

/** Whether the reader holds the same say as the people planning, and may edit the record. */
export function viewerIsPrincipal(snapshot: Pick<PlanSnapshot, 'people'>): boolean {
  return snapshot.people.some((person) => person.self && person.sameSay);
}

/** The thread the address chooses. See the header comment above. */
export function threadFor(
  threads: readonly Thread[],
  choice: ThreadChoice,
): Thread | null {
  const { topic, room, issue } = choice;
  if (issue !== null) {
    return issueThreadOf(threads, issue);
  }
  if (topic === null) {
    return ownThreadOf(threads, null);
  }
  if (room === 'private') {
    return ownThreadOf(threads, topic);
  }
  if (room === 'principals') {
    return principalsThreadOf(threads, topic);
  }
  return (
    groupThreadOf(threads, topic) ??
    principalsThreadOf(threads, topic) ??
    ownThreadOf(threads, topic)
  );
}

function compare(a: Thread, b: Thread): number {
  if (a.createdAt !== b.createdAt) {
    return a.createdAt < b.createdAt ? -1 : 1;
  }
  return a.id < b.id ? -1 : 1;
}

export type MessagePages = {
  readonly messages: readonly Message[];
  readonly nextBefore: string | null;
  readonly depth: number;
};

/**
 * One thread's messages, `depth` pages of them, oldest first.
 *
 * Pulled out of `loadSnapshot` so `usePrivateThread` can read a second thread the same
 * way — same pagination, same "no merge, replace the lot" discipline — without a second
 * copy of this loop.
 */
export async function readMessagePages(
  client: ApiClient,
  threadId: string,
  depth: number,
): Promise<MessagePages> {
  const pages = Math.max(1, depth);
  // Oldest-first at the end, so the pages are read newest-first and reversed once.
  const collected: Message[][] = [];
  let cursor: string | null = null;
  let read = 0;
  for (; read < pages; read += 1) {
    const page: { messages: readonly Message[]; nextBefore: string | null } =
      await client.readMessages(threadId, cursor);
    collected.push([...page.messages]);
    cursor = page.nextBefore;
    if (cursor === null) {
      read += 1;
      break;
    }
  }
  collected.reverse();
  return {
    messages: Object.freeze(collected.flat()),
    nextBefore: cursor,
    depth: Math.max(1, read),
  };
}

export const loadSnapshot: LoadSnapshot = async (client, planId, depth, choice) => {
  const [plan, threads, people, decisions, objectives, pendingActions, topics] =
    await Promise.all([
      client.readPlan(planId),
      client.listThreads(planId),
      client.listPeople(planId),
      client.listDecisions(planId),
      client.listObjectives(planId),
      client.listPendingActions(planId),
      client.listTopics(planId),
    ]);

  const thread = threadFor(threads, choice);
  if (thread === null) {
    return Object.freeze({
      plan,
      threads,
      thread: null,
      messages: Object.freeze([]),
      people,
      decisions,
      objectives,
      topics,
      pendingActions,
      nextBefore: null,
      depth: Math.max(1, depth),
    });
  }

  const pages = await readMessagePages(client, thread.id, depth);

  return Object.freeze({
    plan,
    threads,
    thread,
    messages: pages.messages,
    people,
    decisions,
    objectives,
    topics,
    pendingActions,
    nextBefore: pages.nextBefore,
    depth: pages.depth,
  });
};

/**
 * What the local list looks like after a message is posted, before the refetch lands.
 *
 * THE ONE PIECE OF OPTIMISTIC STATE THIS CLIENT HOLDS, and it is not optimistic in the
 * usual sense: what gets inserted is the message the SERVER returned from the POST, with
 * the server's `seq`, the server's `createdAt` and the server's `authorId`. Nothing here
 * constructs any of them. A client that built a placeholder would be inventing a sequence
 * number and a timestamp, both of which the design forbids by name, and would then have to
 * reconcile the placeholder with the real row — the merge logic that is forbidden
 * separately.
 *
 * Insertion is by id, so the refetch that follows a moment later replaces this list with
 * the server's and produces no duplicate whether it arrives before or after the signal.
 * Acceptance case 3 is the test of exactly this pair of lines.
 */
export type AfterPost = (
  messages: readonly Message[],
  posted: Message,
) => readonly Message[];

export const afterPost: AfterPost = (messages, posted) => {
  if (messages.some((message) => message.id === posted.id)) {
    return messages;
  }
  return Object.freeze([...messages, posted]);
};
